import { useEffect, useMemo, useRef, useState } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import SolarPanelMockup from "./SolarPanelMockup";
import RoofMesh3DViewer from "./RoofMesh3DViewer";
import type {
  GeoJSONFeature,
  GeoJSONFeatureCollection,
  GoogleSolarInsights,
} from "@/api/types";
import type { LatLng } from "@/utils/solarLayoutOverlay";
import type { MeshGroundFrame, MeshWorldCenter } from "@/utils/satelliteGroundPlane";
import { type RoofMeshLayout } from "@/utils/roofMeshPanelPlacement";
import { preferredSessionMount, visibleLayoutForPackage } from "@/utils/panelLayoutSession";
import { useRoofMeshPlacement } from "@/hooks/useRoofMeshPlacement";
import type { SatellitePanelSnapshot } from "@/utils/satellitePanelOverlay";

interface SolarVisualizationProps {
  insights: GoogleSolarInsights;
  propertyBoundary?: GeoJSONFeature;
  viableRoofPolygons?: GeoJSONFeatureCollection;
  satelliteImageUrl?: string | null;
  mapCenter?: LatLng | null;
  mapZoom?: number | null;
  mapSize?: string | null;
  address?: string;
  postcode?: string;
  outlineSource?: string | null;
  inspireCoverage?: "unavailable" | "available";
  mesh3dUrl?: string | null;
  groundFrame?: MeshGroundFrame | null;
  className?: string;
  onLayout?: (layout: RoofMeshLayout) => void;
  onFittedCount?: (count: number) => void;
  /** Package panel count. Omit to draw every module the roof can hold. */
  visiblePanelCount?: number | null;
  /** Visible module count after a 3D click places, reveals, or removes a panel. */
  onPlacedPanelCount?: (count: number) => void;
  onSatellitePlacement?: (snapshot: SatellitePanelSnapshot | null) => void;
  onClearCache?: () => void;
  clearingCache?: boolean;
  /** Mesh URL is still being built. A missing URL after this is a finished result. */
  meshPending?: boolean;
  /** Bump after reset or cache clear so the automatic layout is shown again. */
  layoutRevision?: number;
}

/**
 * Customer solar layout: satellite photo with the title boundary, and the 3D model.
 */
export default function SolarVisualization({
  insights,
  propertyBoundary,
  viableRoofPolygons,
  satelliteImageUrl,
  mapCenter,
  mapZoom,
  mapSize,
  address,
  postcode,
  outlineSource,
  inspireCoverage,
  mesh3dUrl,
  groundFrame,
  className,
  onLayout,
  onFittedCount,
  visiblePanelCount,
  onPlacedPanelCount,
  onSatellitePlacement,
  onClearCache,
  clearingCache = false,
  meshPending = false,
  layoutRevision = 0,
}: SolarVisualizationProps) {
  const [activeTab, setActiveTab] = useState<"2d" | "3d">("2d");
  const [controlsOpen, setControlsOpen] = useState(false);
  const [keep3dMounted, setKeep3dMounted] = useState(false);
  const [editedLayout, setEditedLayout] = useState<RoofMeshLayout | null>(null);
  const [viewerAlign, setViewerAlign] = useState<MeshWorldCenter | null>(null);
  const placement = useRoofMeshPlacement({
    meshUrl: mesh3dUrl,
    groundFrame,
    propertyBoundary,
    satelliteImageUrl,
  });
  const automaticLayout = placement?.layouts.portrait ?? null;
  const sessionMount = preferredSessionMount(mesh3dUrl);
  const sessionDisplayed = useMemo(() => {
    if (!automaticLayout) return null;
    return visibleLayoutForPackage(
      automaticLayout,
      mesh3dUrl,
      sessionMount,
      visiblePanelCount,
    );
  }, [automaticLayout, mesh3dUrl, sessionMount, visiblePanelCount, layoutRevision]);
  // The viewer already chooses which modules are on screen, including ones
  // placed by a click. Limiting that set again would hide the new module.
  const displayedLayout = editedLayout ?? sessionDisplayed;
  const align = placement?.align ?? viewerAlign;
  const onLayoutRef = useRef(onLayout);
  const onFittedCountRef = useRef(onFittedCount);
  const onSatellitePlacementRef = useRef(onSatellitePlacement);
  onLayoutRef.current = onLayout;
  onFittedCountRef.current = onFittedCount;
  onSatellitePlacementRef.current = onSatellitePlacement;
  const fittedCount = placement?.layouts.portrait.panels.length ?? null;
  const coverageUnavailable = inspireCoverage === "unavailable";

  useEffect(() => {
    setEditedLayout(null);
    setViewerAlign(null);
  }, [mesh3dUrl, layoutRevision]);

  useEffect(() => {
    // Let the session snapshot drive 2D until the 3D viewer publishes again.
    setEditedLayout(null);
  }, [visiblePanelCount]);

  useEffect(() => {
    // The 3D view reports its own fitted count once it has placed panels,
    // including after a portrait or landscape change.
    if (editedLayout || fittedCount == null) return;
    onFittedCountRef.current?.(fittedCount);
  }, [editedLayout, fittedCount]);

  useEffect(() => {
    if (displayedLayout) onLayoutRef.current?.(displayedLayout);
  }, [displayedLayout]);

  useEffect(() => {
    if (!displayedLayout || !align || !groundFrame || !satelliteImageUrl) {
      onSatellitePlacementRef.current?.(null);
      return;
    }
    onSatellitePlacementRef.current?.({
      satelliteImageUrl,
      panels: displayedLayout.panels,
      align,
      groundFrame,
    });
  }, [displayedLayout, align, groundFrame, satelliteImageUrl]);

  const satellite = (
    <SolarPanelMockup
      insights={insights}
      propertyBoundary={propertyBoundary}
      viableRoofPolygons={viableRoofPolygons}
      satelliteImageUrl={satelliteImageUrl}
      mapCenter={mapCenter}
      mapZoom={mapZoom}
      mapSize={mapSize}
      address={address}
      postcode={postcode}
      outlineSource={outlineSource}
      panels={displayedLayout?.panels}
      groundAlign={align}
      groundFrame={groundFrame}
    />
  );

  if (coverageUnavailable) {
    return (
      <div className={className}>
        {onClearCache && (
          <div className="mb-3 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClearCache}
              disabled={clearingCache}
              className="rounded-md border border-[#111111]/15 bg-white px-3 py-1.5 text-xs font-semibold text-[#333333] hover:bg-[#f4f4f4] disabled:cursor-wait disabled:opacity-60"
            >
              {clearingCache ? "Redrawing…" : "Clear cache"}
            </button>
          </div>
        )}
        <p className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
          Property boundary data is not available for this address yet, so the property cannot be mapped to its title. Only the satellite view is shown.
        </p>
        {satellite}
      </div>
    );
  }

  return (
    <div className={className}>
      <Tabs
        value={activeTab}
        onValueChange={(value) => {
          const next = value as "2d" | "3d";
          if (next === "3d") setKeep3dMounted(true);
          setActiveTab(next);
        }}
      >
        <div className="mb-3 flex items-center justify-between gap-3">
          <TabsList>
            <TabsTrigger value="2d">Satellite View</TabsTrigger>
            <TabsTrigger value="3d">3D View</TabsTrigger>
          </TabsList>
          {onClearCache && (
            <button
              type="button"
              onClick={onClearCache}
              disabled={clearingCache}
              className="rounded-md border border-[#111111]/15 bg-white px-3 py-1.5 text-xs font-semibold text-[#333333] hover:bg-[#f4f4f4] disabled:cursor-wait disabled:opacity-60"
            >
              {clearingCache ? "Redrawing…" : "Clear cache"}
            </button>
          )}
        </div>

        <TabsContent value="2d" className="mt-0">
          {satellite}
        </TabsContent>

        <TabsContent
          value="3d"
          forceMount={keep3dMounted || undefined}
          className={`mt-0 ${activeTab === "3d" ? "" : "hidden"}`}
        >
          <div className="space-y-3">
            <p className="text-sm text-[#666666]">
              Interactive 3D model of your building from elevation data.
            </p>

            <RoofMesh3DViewer
              meshUrl={mesh3dUrl ?? null}
              satelliteImageUrl={satelliteImageUrl ?? null}
              groundFrame={groundFrame ?? null}
              propertyBoundary={propertyBoundary ?? null}
              width={800}
              height={600}
              onLayout={setEditedLayout}
              onAlign={setViewerAlign}
              onFittedCount={(count) => onFittedCountRef.current?.(count)}
              visiblePanelCount={visiblePanelCount}
              onPlacedPanelCount={onPlacedPanelCount}
              pending={meshPending}
              layoutRevision={layoutRevision}
            />

            <div className="rounded-lg border border-[rgba(17,17,17,0.12)] bg-white text-left">
              <Collapsible open={controlsOpen} onOpenChange={setControlsOpen}>
                <CollapsibleTrigger className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left">
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-[#111111]">
                      3D Building Model with Solar Panels
                    </span>
                    <span className="mt-1 block text-sm leading-relaxed text-[#333333]">
                      Panels sit on roof faces with room for at least two modules.
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-1.5 pt-0.5 text-xs font-semibold text-[#333333]">
                    {controlsOpen ? "Hide controls" : "Show controls"}
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                      className={`transition-transform ${controlsOpen ? "rotate-180" : ""}`}
                    >
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </span>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <ul className="m-0 list-disc space-y-1 px-4 pb-3 pl-9 text-sm leading-relaxed text-[#333333]">
                    <li>Click and drag to rotate. Scroll to zoom. Reset View returns the camera.</li>
                    <li>Click a panel to select its row. Double-click a panel to select that panel on its own.</li>
                    <li>Shift-click another row on the same roof face to add or remove it from the selection.</li>
                    <li>Drag the ring to turn the selection. Arrow keys slide it along the roof. Hold Shift with the arrows for a larger step.</li>
                    <li>Ctrl-click a panel, or press Remove, to take it off. Command-click does the same on a Mac. Undo restores the last removal.</li>
                    <li>Click an empty roof cell beside a panel to add a module on that grid.</li>
                    <li>Height map shows the elevation points. Portrait and Landscape change how the panels sit on the roof.</li>
                  </ul>
                </CollapsibleContent>
              </Collapsible>
              {displayedLayout && (
                <div className="border-t border-[rgba(17,17,17,0.08)] px-4 py-3">
                  <p className="m-0 mb-2 text-sm font-semibold text-[#111111]">Panel Distribution</p>
                  <div className="flex flex-wrap gap-2 text-sm text-[#111111]">
                    {displayedLayout.facetCounts.map((count, index) => (
                      count > 0 ? (
                        <span key={index} className="rounded-md bg-[#F4F4F4] px-2.5 py-1">
                          Roof {index + 1}: <span className="font-semibold">{count} panels</span>
                        </span>
                      ) : null
                    ))}
                  </div>
                  <p className="m-0 mt-2 text-sm font-semibold text-[#111111]">
                    Total: {displayedLayout.panels.length} panels · {displayedLayout.totalKwp.toFixed(1)} kWp
                  </p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
